/**
 * OverviewBody — the Správa firmy "Přehled" hub (`/management`).
 *
 * Company administration only (Členové, EBM, Premier, Servis e-maily) — platform
 * administration lives at `/platforma`. The module-owned
 * tools that used to live here (ceníky, SPZ, LMS, deduplikace) moved to
 * /vozidla/sprava and /zakaznici/sprava — the footer points migrating
 * muscle-memory there. Shared chrome (Shell + Crown + Subtabs) is owned by
 * `ManagementOverviewPage`.
 */

import { Link, useNavigate } from '@tanstack/react-router';
import { Icon, type IconName } from '@/components/Icon';
import { Skeleton } from '@/components/Skeleton';
import { formatDate, formatInt } from '@/lib/format';
import { historieZmenaText, useHistorieLatestQuery } from '@/modules/historie/api';
import { useFirmaMembersQuery } from './api';
import type { ManagementTab } from './helpers';

interface OverviewBodyProps {
  /** Company admin (spravce) or platform admin. */
  canManage: boolean;
}

export function OverviewBody({ canManage }: OverviewBodyProps) {
  const navigate = useNavigate();
  const members = useFirmaMembersQuery(canManage);
  const historie = useHistorieLatestQuery('firma', 8, canManage);

  const openTab = (tab: ManagementTab) => () =>
    navigate({ to: '/management', search: { tab } });

  return (
    <div
      data-testid="management-overview"
      className="flex min-h-0 flex-1 flex-col gap-5 overflow-auto px-8 pb-8 pt-5"
    >
      <section className="grid gap-4 lg:grid-cols-2">
        <NavCard
          icon="users"
          title="Členové"
          description="Práva modulů pro každého člena firmy — čtení, zápis, správa — a příznak správce. Každá změna se ukládá do historie."
          action="Otevřít členy →"
          onClick={openTab('clenove')}
          meta={
            members.isLoading ? (
              <Skeleton className="h-4 w-24" />
            ) : members.data ? (
              <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                {formatInt(members.data.members.length)} členů
              </span>
            ) : undefined
          }
        />
        <NavCard
          icon="chart"
          title="EBM zařazení"
          description="Výsledovka Ford (výkaz i rozvaha): nahrání exportu, přiřazení nových účtů k OZN a verzované mapování."
          action="Otevřít EBM zařazení →"
          onClick={openTab('ebm')}
          meta={
            <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
              Ford · výsledovka
            </span>
          }
        />
        <NavCard
          icon="external"
          title="EBM report"
          description="Náklady a výnosy po OZN spočtené z nahraných výkazů, s meziměsíčním porovnáním."
          action="Otevřít EBM report →"
          onClick={openTab('ebm-report')}
          meta={
            <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
              Ford · OZN
            </span>
          }
        />
        <NavCard
          icon="building"
          title="Premier"
          description="Vyhledání partnera v účetním systému Premier podle názvu nebo IČO a stav spojení."
          action="Otevřít Premier →"
          onClick={openTab('premier')}
          meta={
            <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
              Účetnictví
            </span>
          }
        />
        <NavCard
          icon="mail"
          title="Servis e-maily"
          description="Podpis, telefon a kontaktní e-mail v potvrzeních a připomínkách servisních termínů."
          action="Otevřít nastavení →"
          onClick={openTab('servis-email')}
          meta={
            <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
              Servis
            </span>
          }
        />
        <NavCard
          icon="mail"
          title="Nabídky"
          description="Počet dnů, po které platí cenová nabídka — vytištěný údaj „Platnost nabídky“."
          action="Otevřít nastavení →"
          onClick={openTab('nabidky')}
          meta={
            <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
              Vozidla
            </span>
          }
        />
      </section>

      <p className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
        Ceníky, párování SPZ a LMS najdete ve{' '}
        <Link to="/vozidla/sprava" className="text-accent-text hover:underline">
          Správě vozidel
        </Link>
        ; deduplikaci zákazníků ve{' '}
        <Link to="/zakaznici/sprava" className="text-accent-text hover:underline">
          Správě zákazníků
        </Link>
        .
      </p>

      {canManage && (
        <section
          data-testid="management-activity"
          className="border border-line bg-paper"
        >
          <header className="flex items-center gap-2 border-b border-line px-5 py-3.5 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-3">
            <span aria-hidden className="inline-block h-px w-3.5 bg-accent" />
            <span>Historie změn</span>
            <Link
              to="/management"
              search={{ tab: 'historie' }}
              className="ml-auto text-[10px] text-accent-text hover:underline"
            >
              Celá historie →
            </Link>
          </header>
          {historie.isLoading ? (
            <div className="flex flex-col gap-2 p-5">
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-full" />
            </div>
          ) : !historie.data || historie.data.zaznamy.length === 0 ? (
            <p className="px-5 py-6 text-center font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
              Žádné změny dosud neproběhly
            </p>
          ) : (
            <ul className="divide-y divide-line">
              {historie.data.zaznamy.map((z) => (
                <li
                  key={z.klic}
                  className="flex items-baseline gap-4 px-5 py-3 text-[12.5px]"
                >
                  <span className="font-mono text-[11px] tabular-nums tracking-[-0.02em] text-ink-3">
                    {formatDate(z.vytvoreno_at) || z.vytvoreno_at.slice(0, 10)}
                  </span>
                  <span className="text-ink">
                    <b className="font-semibold">{z.autor?.jmeno ?? 'Systém'}</b>
                    {' → '}
                    <b className="font-semibold">{z.zaznam_label}</b>
                  </span>
                  <span className="ml-auto truncate text-right font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
                    {z.zmeny.map(historieZmenaText).join(' · ')}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

interface NavCardProps {
  icon: IconName;
  title: string;
  description: string;
  action: string;
  onClick?: () => void;
  meta?: React.ReactNode;
}

function NavCard({ icon, title, description, action, onClick, meta }: NavCardProps) {
  const interactive = !!onClick;
  const Tag = interactive ? 'button' : 'div';
  return (
    <Tag
      type={interactive ? 'button' : undefined}
      onClick={onClick}
      className={[
        'group block w-full border border-line bg-paper px-6 py-5 text-left transition-colors',
        interactive ? 'cursor-pointer hover:border-ink hover:bg-surface-2' : 'cursor-default',
      ].join(' ')}
    >
      <div className="mb-3 flex items-center gap-3">
        <span
          aria-hidden
          className="grid size-9 place-items-center border border-line bg-bg text-ink-2 group-hover:border-ink group-hover:text-ink"
        >
          <Icon name={icon} size={16} />
        </span>
        {meta && <span className="ml-auto">{meta}</span>}
      </div>
      <h2 className="font-display text-[24px] leading-none tracking-[-0.02em] text-ink">
        {title}
      </h2>
      <p className="mt-2 text-[12.5px] leading-[1.5] text-ink-3">{description}</p>
      <p
        className={
          'mt-4 font-mono text-[10.5px] uppercase tracking-[0.08em] ' +
          (interactive ? 'text-accent-text' : 'text-ink-3')
        }
      >
        {action}
      </p>
    </Tag>
  );
}
